import { useDrag, useDrop } from 'react-dnd';
import { useTranslation } from 'react-i18next';
import { Link, To } from 'react-router-dom';
import * as React from 'react';
import { useId, useRef, useState } from 'react';
import { Slot } from '@radix-ui/react-slot';
import {
    faArrowDown,
    faArrowUp,
    faChevronDown,
    faChevronRight,
    faChevronUp,
    faEllipsis,
    faGripDotsVertical,
    faPlus,
    faTriangleExclamation,
    faXmark,
} from '@fortawesome/pro-regular-svg-icons';
import Button from '../../core/components/Button.tsx';
import ConfirmModal from '../../core/components/ConfirmModal.tsx';
import DropdownMenu from '../../core/components/DropdownMenu.tsx';
import { withIcon } from '../../core/components/Icon.tsx';
import SaveStatus from '../../core/components/SaveStatus.tsx';
import { SettingsCard } from '../../core/components/SettingsFormCard.tsx';
import SwitchRow from '../../core/components/SwitchRow.tsx';
import StringField from '../../core/components/Fields/StringField.tsx';
import DecimalField from '../../core/components/Fields/DecimalField.tsx';
import DateField from '../../core/components/Fields/DateField.tsx';
import BooleanField from '../../core/components/Fields/BooleanField.tsx';
import IntegerField from '../../core/components/Fields/IntegerField.tsx';
import TagsField from '../../core/components/Fields/TagsField.tsx';
import JsonField from '../../core/components/Fields/JsonField.tsx';
import classnames from '../../core/utils/classnames.tsx';

// based on ValueType from backend
const FIELDS = {
    string: StringField,
    integer: IntegerField,
    float: DecimalField,
    decimal: DecimalField,
    boolean: BooleanField,
    date: DateField,
    datetime: DateField,
    dict: JsonField,
    list: TagsField,
};

const DragIcon = withIcon(faGripDotsVertical);
const AddIcon = withIcon(faPlus);
const RemoveIcon = withIcon(faXmark);
const UpIcon = withIcon(faArrowUp);
const DownIcon = withIcon(faArrowDown);
const ChevronIcon = withIcon(faChevronRight);
const ChevronDownIcon = withIcon(faChevronDown);
const ChevronUpIcon = withIcon(faChevronUp);
const WarningIcon = withIcon(faTriangleExclamation);
const EllipsisIcon = withIcon(faEllipsis);

interface DraggedRow {
    name: string;
    index: number;
}

// moves the dragged row to a row once the pointer passes that row's middle
const useDragDrop = ({
    name,
    index,
    itemType,
    onMove,
    onDragEnd,
}: {
    name: string;
    index: number;
    itemType: string;
    onMove: (name: string, target: string) => void;
    onDragEnd: (name: string, isDropped: boolean) => void;
}) => {
    const dragRef = useRef<HTMLSpanElement>(null);
    const itemRef = useRef<HTMLDivElement>(null);

    const [, drop] = useDrop({
        accept: itemType,
        hover: (item: DraggedRow, monitor) => {
            if (!itemRef.current || item.index === index) return;
            const rect = itemRef.current.getBoundingClientRect();
            const pointerY = monitor.getClientOffset().y - rect.top;
            const middleY = (rect.bottom - rect.top) / 2;
            if ((item.index < index && pointerY < middleY) || (item.index > index && pointerY > middleY)) return;
            onMove(item.name, name);
            item.index = index;
        },
    });
    const [{ isDragging }, drag] = useDrag({
        type: itemType,
        item: { name, index },
        end: (item, monitor) => onDragEnd(item.name, monitor.didDrop()),
        collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    });

    drag(dragRef);
    drop(itemRef);
    return { dragRef, itemRef, isDragging };
};

export interface FieldsConfigRowData {
    name: string;
    label: string;
    note?: string;
    assistanceCount?: number;
    assistanceHint?: string;
}

interface FieldsConfigRowProps {
    to: To;
    labels: { moveUp: string; moveDown: string; remove: string };
    // a reorderable row has a drag handle and ↑ ↓; an arrow without a neighbour is disabled
    isReorderable: boolean;
    onMoveUp?: () => void;
    onMoveDown?: () => void;
    onRemove?: () => void;
    // why remove is disabled, like for a field's last subfield
    removeDisabledHint?: string;
    aside?: React.ReactNode;
    dragRef?: React.RefObject<HTMLSpanElement>;
    itemRef?: React.RefObject<HTMLDivElement>;
    isDragging?: boolean;
    children: React.ReactNode;
}

// a row that links to its page; the drag handle, arrows and remove show on hover
const FieldsConfigRow = ({
    to,
    labels,
    isReorderable,
    onMoveUp = undefined,
    onMoveDown = undefined,
    onRemove = undefined,
    removeDisabledHint = undefined,
    aside = undefined,
    dragRef = undefined,
    itemRef = undefined,
    isDragging = false,
    children,
}: FieldsConfigRowProps) => (
    <div
        ref={itemRef}
        className={classnames(
            'group relative flex items-center gap-1 pr-4 text-sm transition-colors hover:bg-surface-medium',
            isReorderable ? 'pl-1' : 'pl-4',
            isDragging && 'opacity-40'
        )}
    >
        {/* 8px wide (the icon is 7px), so the label starts 16px in */}
        {isReorderable && (
            <span
                ref={dragRef}
                className="relative z-10 flex w-2 flex-none cursor-grab justify-center text-content-medium opacity-0 transition-opacity group-hover:opacity-100"
            >
                <DragIcon />
            </span>
        )}
        <Link
            to={to}
            draggable={false}
            className="flex min-w-0 flex-1 py-3 after:absolute after:inset-0 focus-visible:outline-none focus-visible:after:ring-2 focus-visible:after:ring-inset focus-visible:after:ring-line-brand-strong"
        >
            {children}
        </Link>
        {/* no room taken while hidden, so the name isn't cut short */}
        {(isReorderable || onRemove) && (
            <span className="relative z-10 hidden flex-none items-center gap-1 group-hover:flex group-focus-within:flex">
                {isReorderable && (
                    <>
                        <Button
                            variant="ghost"
                            className="!p-0 w-8 h-8"
                            type="button"
                            aria-label={labels.moveUp}
                            disabled={!onMoveUp}
                            onClick={onMoveUp}
                        >
                            <UpIcon />
                        </Button>
                        <Button
                            variant="ghost"
                            className="!p-0 w-8 h-8"
                            type="button"
                            aria-label={labels.moveDown}
                            disabled={!onMoveDown}
                            onClick={onMoveDown}
                        >
                            <DownIcon />
                        </Button>
                    </>
                )}
                {onRemove && (
                    <Button
                        variant="ghost"
                        className="!p-0 w-8 h-8"
                        type="button"
                        aria-label={labels.remove}
                        title={removeDisabledHint}
                        disabled={!!removeDisabledHint}
                        onClick={onRemove}
                    >
                        <RemoveIcon />
                    </Button>
                )}
            </span>
        )}
        {aside}
        <ChevronIcon className="ml-2 flex-none text-content-medium" />
    </div>
);

const FieldsConfigDraggableRow = ({
    name,
    index,
    itemType,
    onMove,
    onDragEnd,
    ...props
}: Omit<FieldsConfigRowProps, 'dragRef' | 'itemRef' | 'isDragging'> & {
    name: string;
    index: number;
    itemType: string;
    onMove: (name: string, target: string) => void;
    onDragEnd: (name: string, isDropped: boolean) => void;
}) => {
    const drag = useDragDrop({ name, index, itemType, onMove, onDragEnd });
    return <FieldsConfigRow {...props} {...drag} />;
};

interface FieldsConfigOrderedRowsProps<Row extends { name: string }> {
    rows: Row[];
    toRow: (name: string) => To;
    // reorderable when given; the page around provides the DndProvider
    onMove?: (name: string, target: string) => void;
    onRemove?: (name: string) => void;
    rowProps: (row: Row) => Pick<FieldsConfigRowProps, 'labels' | 'removeDisabledHint' | 'aside' | 'children'>;
}

// the rows of a list, with ↑ ↓ moving a row to its neighbour's place; rows are dragged only within their own list
const FieldsConfigOrderedRows = <Row extends { name: string }>({
    rows,
    toRow,
    onMove = undefined,
    onRemove = undefined,
    rowProps,
}: FieldsConfigOrderedRowsProps<Row>) => {
    const itemType = useId();
    // the order while a row is dragged; the move reaches onMove only once the row is dropped, so the page doesn't show
    // Cancel / Save for every row passed on the way
    const [dragOrder, setDragOrder] = useState<string[] | null>(null);
    const shownRows = dragOrder ? dragOrder.map((name) => rows.find((row) => row.name === name)) : rows;

    const handleDragMove = (name: string, target: string) =>
        setDragOrder((order) => {
            const current = order ?? rows.map((row) => row.name);
            const rest = current.filter((item) => item !== name);
            const index = current.indexOf(target);
            return [...rest.slice(0, index), name, ...rest.slice(index)];
        });

    // onMove puts a row in its target's place, so the target is the row that had the dropped row's new place
    const handleDragEnd = (name: string, isDropped: boolean) => {
        const target = dragOrder && rows[dragOrder.indexOf(name)]?.name;
        setDragOrder(null);
        if (isDropped && target && target !== name) onMove(name, target);
    };

    return shownRows.map((row, index) => {
        const previous = shownRows[index - 1]?.name;
        const next = shownRows[index + 1]?.name;
        const props: FieldsConfigRowProps = {
            ...rowProps(row),
            to: toRow(row.name),
            isReorderable: !!onMove,
            onMoveUp: onMove && previous ? () => onMove(row.name, previous) : undefined,
            onMoveDown: onMove && next ? () => onMove(row.name, next) : undefined,
            onRemove: onRemove && (() => onRemove(row.name)),
        };
        return onMove ? (
            <FieldsConfigDraggableRow
                key={row.name}
                {...props}
                name={row.name}
                index={index}
                itemType={itemType}
                onMove={handleDragMove}
                onDragEnd={handleDragEnd}
            />
        ) : (
            <FieldsConfigRow key={row.name} {...props} />
        );
    });
};

export interface FieldsConfigAdding {
    items: { name: string; label: string }[];
    // the grey row's label, like "9 more fields available"
    label: string;
    isOpen: boolean;
    onOpenChange: (isOpen: boolean) => void;
    onAdd: (name: string) => void;
    // a custom field, from a button below the list: it opens its own page instead of adding a row
    onAddCustom?: () => void;
}

interface FieldsConfigListProps {
    rows: FieldsConfigRowData[];
    toRow: (name: string) => To;
    onMove?: (name: string, target: string) => void;
    onRemove?: (name: string) => void;
    adding?: FieldsConfigAdding;
}

// a section's fields, each a link to its page with a two-line name and note; fields from the list are added from the
// grey row at the bottom, as subfields are, and a custom field with a button below the list
const FieldsConfigList = ({
    rows,
    toRow,
    onMove = undefined,
    onRemove = undefined,
    adding = undefined,
}: FieldsConfigListProps) => {
    const { t } = useTranslation('config');

    const hasItems = adding?.items.length > 0;
    const addButton = adding?.onAddCustom && (
        <Button type="button" variant="outline" className="self-start" onClick={adding.onAddCustom}>
            <AddIcon /> {t(hasItems ? 'configPage.document.addCustomFieldLabel' : 'configPage.document.addFieldLabel')}
        </Button>
    );
    const addRow = hasItems && <FieldsConfigAddMore key="add" {...adding} />;

    if (!rows.length && !addRow) return addButton || null;

    const card = (
        <FieldsConfigCard hasFullWidthDividers>
            <FieldsConfigOrderedRows
                rows={rows}
                toRow={toRow}
                onMove={onMove}
                onRemove={onRemove}
                rowProps={(row) => ({
                    labels: {
                        moveUp: t('configPage.document.form.moveFieldUp', { field: row.label }),
                        moveDown: t('configPage.document.form.moveFieldDown', { field: row.label }),
                        remove: t('configPage.document.form.removeField', { field: row.label }),
                    },
                    aside: row.assistanceCount > 0 && (
                        <span
                            title={row.assistanceHint}
                            aria-label={row.assistanceHint}
                            className="ml-2 flex flex-none items-center gap-1 text-xs font-medium text-content-warning-strong"
                        >
                            <WarningIcon />
                            {row.assistanceCount}
                        </span>
                    ),
                    children: (
                        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                            <span className="truncate font-medium text-content-strong">{row.label}</span>
                            <span className="truncate text-xs text-content-medium">{row.note}</span>
                        </span>
                    ),
                })}
            />
            {addRow}
        </FieldsConfigCard>
    );
    return (
        <div className="flex flex-col gap-3">
            {card}
            {addButton}
        </div>
    );
};

export interface FieldsConfigSubfieldRowData {
    name: string;
    label: string;
    isRequired: boolean;
}

interface FieldsConfigSubfieldsProps {
    fieldLabel: string;
    rows: FieldsConfigSubfieldRowData[];
    toRow: (name: string) => To;
    onMove: (name: string, target: string) => void;
    onRemove: (name: string) => void;
    adding: FieldsConfigAdding;
}

// a field's subfields like the fields in the list, the name and Required on one line
const FieldsConfigSubfields = ({ fieldLabel, rows, toRow, onMove, onRemove, adding }: FieldsConfigSubfieldsProps) => {
    const { t } = useTranslation('config');
    return (
        <FieldsConfigSection
            title={t('configPage.document.form.subfields')}
            description={t('configPage.document.form.subfieldsDescription', { field: fieldLabel })}
        >
            <FieldsConfigCard hasFullWidthDividers>
                <FieldsConfigOrderedRows
                    rows={rows}
                    toRow={toRow}
                    onMove={onMove}
                    onRemove={(name) => {
                        onRemove(name);
                        // so the removed subfield is right there to add back
                        adding.onOpenChange(true);
                    }}
                    rowProps={(row) => ({
                        labels: {
                            moveUp: t('configPage.document.form.moveSubfieldUp', { subfield: row.label }),
                            moveDown: t('configPage.document.form.moveSubfieldDown', { subfield: row.label }),
                            remove: t('configPage.document.form.removeSubfield', { subfield: row.label }),
                        },
                        removeDisabledHint: rows.length === 1 ? t('configPage.document.form.lastSubfield') : undefined,
                        children: (
                            <span className="flex min-w-0 flex-1 items-baseline gap-2">
                                <span className="truncate font-medium text-content-strong">{row.label}</span>
                                {row.isRequired && (
                                    <span className="flex-none text-content-medium">
                                        {t('configPage.document.fieldNotes.required')}
                                    </span>
                                )}
                            </span>
                        ),
                    })}
                />
                <FieldsConfigAddMore key="add" {...adding} />
            </FieldsConfigCard>
        </FieldsConfigSection>
    );
};

interface FieldsConfigSectionProps {
    title: string;
    description?: string;
    aside?: React.ReactNode;
    children: React.ReactNode;
}

const FieldsConfigSection = ({
    title,
    description = undefined,
    aside = undefined,
    children,
}: FieldsConfigSectionProps) => (
    <section className="flex flex-col gap-3">
        <div className="flex items-baseline justify-between gap-3">
            <div className="flex flex-col">
                <h3 className="text-sm font-semibold text-content-strong">{title}</h3>
                {description && <p className="text-sm text-content-medium">{description}</p>}
            </div>
            {aside && <span className="flex flex-none items-center gap-3 text-xs text-content-medium">{aside}</span>}
        </div>
        {children}
    </section>
);

interface FieldsConfigMoreRowProps {
    isOpen: boolean;
    label: string;
    onClick: () => void;
    // the rest is something to add (fields, subfields), not something folded away (settings)
    isAdding?: boolean;
}

// the grey last row of a card that shows or hides the rest: "3 more settings available" ⌄ / "Show less" ⌃
const FieldsConfigMoreRow = ({ isOpen, label, onClick, isAdding = false }: FieldsConfigMoreRowProps) => {
    const { t } = useTranslation('config');
    return (
        <button
            type="button"
            onClick={onClick}
            aria-expanded={isOpen}
            className="group flex w-full cursor-pointer items-center gap-3 bg-surface-medium px-4 py-3 text-left text-sm transition-colors hover:bg-surface-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-line-brand-strong"
        >
            {isAdding && !isOpen && <AddIcon className="flex-none text-content-medium" />}
            <span className="flex-1 text-content-medium group-hover:text-content-strong">
                {isOpen ? t('configPage.document.form.showLess') : label}
            </span>
            {isOpen ? (
                <ChevronUpIcon className="flex-none text-content-medium" />
            ) : (
                <ChevronDownIcon className="flex-none text-content-medium" />
            )}
        </button>
    );
};

// a list's grey bottom part: "9 more subfields", which opens all that aren't in use as chips
const FieldsConfigAddMore = ({
    items,
    label,
    isOpen,
    onOpenChange,
    onAdd,
}: Omit<FieldsConfigAdding, 'onAddCustom'>) => {
    if (!items.length) return null;

    const moreRow = (
        <FieldsConfigMoreRow isOpen={isOpen} label={label} onClick={() => onOpenChange(!isOpen)} isAdding />
    );
    if (!isOpen) return moreRow;

    return (
        <div className="flex flex-col">
            <div className="flex flex-wrap gap-2 bg-surface-medium px-4 py-3">
                {items.map((item) => (
                    <button
                        key={item.name}
                        type="button"
                        onClick={() => onAdd(item.name)}
                        className="flex items-center gap-1.5 rounded-full border border-solid border-line-weak bg-surface-weak px-3 py-1 text-sm text-content-strong cursor-pointer transition-colors hover:bg-surface-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-line-brand-strong"
                    >
                        <AddIcon className="text-xs text-content-medium" />
                        {item.label}
                    </button>
                ))}
            </div>
            {FULL_WIDTH_DIVIDER}
            {moreRow}
        </div>
    );
};

const FULL_WIDTH_DIVIDER = <div className="border-0 border-t border-solid border-line-subtle" />;

// light dividers between the rows; inset to the rows' text unless the rows run edge to edge themselves
const FieldsConfigRows = ({
    hasFullWidthDividers = false,
    children,
}: {
    hasFullWidthDividers?: boolean;
    children: React.ReactNode;
}) => (
    <>
        {React.Children.toArray(children).map((row, index) => (
            <React.Fragment key={React.isValidElement(row) ? row.key : index}>
                {index > 0 && (
                    <div
                        className={classnames(
                            'border-0 border-t border-solid border-line-subtle',
                            !hasFullWidthDividers && 'mx-4'
                        )}
                    />
                )}
                {row}
            </React.Fragment>
        ))}
    </>
);

interface FieldsConfigCardProps {
    hasFullWidthDividers?: boolean;
    // a grey last row, like "3 more settings available", below a full-width divider
    footer?: React.ReactNode;
    children: React.ReactNode;
}

const FieldsConfigCard = ({ hasFullWidthDividers = false, footer = undefined, children }: FieldsConfigCardProps) => {
    const hasRows = React.Children.toArray(children).length > 0;
    return (
        <div className="flex flex-col overflow-hidden rounded-lg border border-solid border-line-weak">
            <FieldsConfigRows hasFullWidthDividers={hasFullWidthDividers}>{children}</FieldsConfigRows>
            {hasRows && footer && FULL_WIDTH_DIVIDER}
            {footer}
        </div>
    );
};

export interface FieldsConfigAdvancedRow {
    key: string;
    isActive: boolean;
    // shown even while not active, like the settings a custom field can't be saved without
    isAlwaysShown?: boolean;
    node: React.ReactNode;
}

interface FieldsConfigAdvancedOptionsProps {
    title: string;
    rows: FieldsConfigAdvancedRow[];
    // left of the count, like the Support only badge
    badge?: React.ReactNode;
}

// the settings that are on, and a row for the rest; one switched off moves into the rest right away
const FieldsConfigAdvancedOptions = ({ title, rows, badge = undefined }: FieldsConfigAdvancedOptionsProps) => {
    const { t } = useTranslation('config');
    const [isOpen, setOpen] = useState(false);
    const activeCount = rows.filter((row) => row.isActive).length;
    const shown = isOpen ? rows : rows.filter((row) => row.isActive || row.isAlwaysShown);
    const hiddenCount = rows.length - shown.length;

    return (
        <FieldsConfigSection
            title={title}
            aside={
                <>
                    {badge}
                    {t('configPage.document.form.advancedSettingsCount', { on: activeCount, total: rows.length })}
                </>
            }
        >
            <FieldsConfigCard
                footer={
                    (isOpen || hiddenCount > 0) && (
                        <FieldsConfigMoreRow
                            isOpen={isOpen}
                            label={t(
                                shown.length
                                    ? 'configPage.document.form.moreSettings'
                                    : 'configPage.document.form.settingsCount',
                                { count: hiddenCount }
                            )}
                            onClick={() => setOpen(!isOpen)}
                        />
                    )
                }
            >
                {shown.map((row) => (
                    <React.Fragment key={row.key}>{row.node}</React.Fragment>
                ))}
            </FieldsConfigCard>
        </FieldsConfigSection>
    );
};

export interface FieldsConfigBreadcrumbParent {
    label: string;
    onClick: () => void;
}

interface FieldsConfigBreadcrumbProps {
    parents: FieldsConfigBreadcrumbParent[];
    current: string;
}

const FieldsConfigBreadcrumb = ({ parents, current }: FieldsConfigBreadcrumbProps) => (
    <nav className="flex min-w-0 items-center text-sm text-content-strong">
        {parents.map((parent) => (
            <React.Fragment key={parent.label}>
                <button
                    type="button"
                    onClick={parent.onClick}
                    className="-mx-2 flex min-w-0 cursor-pointer items-center rounded px-2 py-1 text-content-medium transition-colors hover:bg-surface-strong hover:text-content-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-line-brand-strong"
                >
                    <span className="truncate">{parent.label}</span>
                </button>
                <ChevronIcon aria-hidden className="mx-3 flex-none text-[9px] text-content-weak" />
            </React.Fragment>
        ))}
        <span className="truncate">{current}</span>
    </nav>
);

interface FieldsConfigPageProps {
    parents: FieldsConfigBreadcrumbParent[];
    current: string;
    // without a title the page only has its breadcrumb, like one for a field that isn't in use
    title?: string;
    // under the title, like "Header · Not saved yet"
    subtitle?: string;
    actions?: React.ReactNode;
    className?: string;
    children?: React.ReactNode;
}

// a page opened from a list (a field, a subfield, a rule): the breadcrumb back, the title, then the page's content
const FieldsConfigPage = ({
    parents,
    current,
    title = undefined,
    subtitle = undefined,
    actions = undefined,
    className = undefined,
    children = undefined,
}: FieldsConfigPageProps) => (
    <SettingsCard className={classnames('gap-5 pb-10', className)}>
        <FieldsConfigBreadcrumb parents={parents} current={current} />
        {title && (
            <div className="flex min-h-[2.125rem] items-center justify-between gap-3">
                <div className="flex min-w-0 flex-col">
                    <h3 className="text-base font-semibold text-content-strong">{title}</h3>
                    {subtitle && <span className="text-xs text-content-medium">{subtitle}</span>}
                </div>
                {actions && <span className="flex flex-none items-center gap-2">{actions}</span>}
            </div>
        )}
        {children}
    </SettingsCard>
);

interface FieldsConfigRemoveMenuProps {
    fieldLabel: string;
    message: string;
    onRemove: () => void;
}

// the ⋯ next to a field page's title, with "Remove field" behind a confirmation
const FieldsConfigRemoveMenu = ({ fieldLabel, message, onRemove }: FieldsConfigRemoveMenuProps) => {
    const { t } = useTranslation('config');
    const [isConfirming, setConfirming] = useState(false);
    return (
        <>
            <DropdownMenu>
                <DropdownMenu.Trigger asChild>
                    <Button type="button" variant="ghost" aria-label={t('configPage.document.moreFieldActions')}>
                        <EllipsisIcon />
                    </Button>
                </DropdownMenu.Trigger>
                <DropdownMenu.Content align="end" className="min-w-48">
                    <DropdownMenu.Item danger onSelect={() => setConfirming(true)}>
                        {t('configPage.document.removeFieldAction')}
                    </DropdownMenu.Item>
                </DropdownMenu.Content>
            </DropdownMenu>
            {isConfirming && (
                <ConfirmModal
                    title={t('configPage.document.removeFieldTitle', { field: fieldLabel })}
                    labelConfirm={t('configPage.document.removeFieldAction')}
                    danger
                    onConfirm={() => {
                        setConfirming(false);
                        onRemove();
                    }}
                    onCancel={() => setConfirming(false)}
                >
                    <p>{message}</p>
                </ConfirmModal>
            )}
        </>
    );
};

interface FieldsConfigSaveBarProps {
    onCancel: () => void;
    onSave: () => unknown;
    isSaving?: boolean;
}

// Cancel and Save at a page's or list's end, once there are changes
const FieldsConfigSaveBar = ({ onCancel, onSave, isSaving = false }: FieldsConfigSaveBarProps) => {
    const { t } = useTranslation('config');
    return (
        <SaveStatus
            isDirty
            isSubmitting={isSaving}
            onSave={onSave}
            className="border-0 border-t border-solid border-line-weak pt-4"
        >
            <Button type="button" variant="outline" onClick={onCancel}>
                {t('configPage.document.cancel')}
            </Button>
        </SaveStatus>
    );
};

interface FieldsConfigOptionsFormFieldProps {
    label: string;
    helpText?: string;
    error?: { message?: string };
    value: unknown;
    onValueChange: (value: unknown) => void;
    onBlur?: (event: unknown) => void;
    defaultValue?: unknown;
    valueType: string;
    nullable: boolean;
    required?: boolean;
    component?: React.ElementType;
    componentProps?: Record<string, unknown>;
}

// a setting in a card's row: a switch row for booleans and settings that can be off, else a label over the field
const FieldsConfigOptionsFormField = ({
    label,
    helpText = undefined,
    error = undefined,
    value,
    onValueChange,
    onBlur = undefined,
    defaultValue = undefined,
    valueType,
    nullable,
    required = false,
    component = undefined,
    componentProps = {},
}: FieldsConfigOptionsFormFieldProps) => {
    const isBoolean = valueType === 'boolean';

    const handleToggleSwitch = (checked: boolean) => {
        if (isBoolean) {
            onValueChange(checked);
        } else if (nullable) {
            onValueChange(checked ? (defaultValue ?? '') : null);
        }
        onBlur?.(null);
    };

    const FieldComponent = component || FIELDS[valueType] || StringField;
    const showField = !isBoolean && (!nullable || value != undefined);
    const labelContent = (
        <>
            {label} {required && <span className="text-content-danger-strong">*</span>}
        </>
    );
    const field = showField && (
        <Slot
            className={classnames(error && 'border-line-danger-default')}
            // @ts-ignore
            required={required}
        >
            <FieldComponent
                {...componentProps}
                value={value}
                onValueChange={onValueChange}
                required={required}
                onBlur={onBlur}
            />
        </Slot>
    );
    const errorMessage = error?.message && <span className="text-content-danger-weak text-xs">{error.message}</span>;
    const hasContent = !!(field || errorMessage);

    if (isBoolean || nullable) {
        return (
            <SwitchRow
                // the row runs to the card's edges; with content, SwitchRow puts the class on its wrapper, and the row
                // inside keeps its own px-3 py-2
                className={hasContent ? 'rounded-none px-1 pt-1' : 'rounded-none px-4 py-3'}
                label={labelContent}
                description={!error?.message && helpText}
                value={isBoolean ? !!value : value != undefined}
                onValueChange={handleToggleSwitch}
            >
                {hasContent && (
                    <>
                        {field}
                        {errorMessage}
                    </>
                )}
            </SwitchRow>
        );
    }

    return (
        <div className="flex flex-col gap-2 px-4 py-3">
            <label className="text-content-strong text-sm">{labelContent}</label>
            {field}
            {!error?.message && helpText && <span className="text-content-medium text-xs">{helpText}</span>}
            {errorMessage}
        </div>
    );
};

export default {
    List: FieldsConfigList,
    Subfields: FieldsConfigSubfields,
    Card: FieldsConfigCard,
    Section: FieldsConfigSection,
    Rows: FieldsConfigRows,
    MoreRow: FieldsConfigMoreRow,
    AdvancedOptions: FieldsConfigAdvancedOptions,
    Breadcrumb: FieldsConfigBreadcrumb,
    Page: FieldsConfigPage,
    RemoveMenu: FieldsConfigRemoveMenu,
    SaveBar: FieldsConfigSaveBar,
    OptionsFormField: FieldsConfigOptionsFormField,
};
